// Batch 15 Step 3c — single source of truth for measurement-type
// picker options. Both ProgramLibrary/Create (program picker) and
// ProgramLibrary/shared/TargetsWorkspace (target override picker)
// read from this file and write the enum key. Neither picker
// maintains its own list — that drift is what Step 3c ends.
//
// Structural invariant: a value with no Recorder cannot appear here.
// The type is `MeasurementType` from `./measurementType`, which is
// the same set of seven enum keys as the DB enum column and the
// dispatch switch. If a new measurement type is added to the module,
// add it to the DB enum, the FE MeasurementType union, the
// SessionData/Create renderMode switch, AND this array — all in the
// same batch (see closed-vocab-consumer-check standing rule).
//
// Rate is deliberately absent because there is no RateRecorder.
// T28 tracks re-adding both together.

import { toMeasurementTypeEnum, type MeasurementType } from "./measurementType";

// Labels chosen to match what MEASUREMENT_LABELS in
// ProgramLibrary/shared/labels.ts would render for the equivalent
// legacy varchar value. Keeping this in sync means users never see a
// picker label spelt differently from an in-row display label.
export type MeasurementTypeOption = {
  value: MeasurementType;   // enum key — what the picker writes
  label: string;            // display text — what users see
};

export const MEASUREMENT_TYPE_OPTIONS: readonly MeasurementTypeOption[] = [
  { value: "trial_percent_correct", label: "Trial Accuracy" },   // MEASUREMENT_LABELS['Percent Correct']
  { value: "task_analysis",         label: "Task Analysis" },
  { value: "frequency",             label: "Frequency" },
  { value: "duration",              label: "Duration" },
  { value: "latency",               label: "Latency" },
  { value: "interval",              label: "Interval Recording" },
  { value: "abc_behaviour",         label: "ABC Behaviour" },
] as const;

// Reverse lookup helper — for hydrating a picker's `value` when the
// row still carries a legacy varchar label (edit-mode load of a row
// authored before Step 3c ships, or from an old client). Callers pass
// the row's `measurement_type` (enum, preferred) OR `measurementType`
// (varchar); the picker's value ends up as the enum key.
export function pickerValueFromRow(row: {
  measurement_type?: string | null;
  measurementType?: string | null;
}): MeasurementType | "" {
  const resolved =
    toMeasurementTypeEnum(row.measurement_type ?? null) ??
    toMeasurementTypeEnum(row.measurementType ?? null);
  return resolved ?? "";
}

// Display label for a given enum key. Used by non-picker read sites
// (e.g. review cards, list cells) that want to render human text
// from an enum column value without reaching into the varchar.
export function measurementTypeLabel(value: MeasurementType | null | undefined): string {
  if (!value) return "—";
  const match = MEASUREMENT_TYPE_OPTIONS.find((o) => o.value === value);
  return match ? match.label : String(value);
}
